(function(angular, _) {
    'use strict';

    angular.module('wtApp.dataService')
        .factory('WtReportAnalysis', ['WtAnalysis', '$injector', function(WtAnalysis, $injector) {
            const ViewMode = $injector.get('ViewMode');

            /**
             * @ngdoc service
             * @name WtReportAnalysis
             * @description Report analysis constructor for analyses added as report elements
             * @TODO WtReportAnalysis > WtAnalysisElement
             */
            return class WtReportAnalysis extends WtAnalysis {
                constructor() {
                    super(...arguments);

                    /**
                     * The hard coded path send when tracking report analysis pages
                     * @type {string}
                     * @private
                     */
                    this.menuPath_ = 'report_element';

                    // Adds older browsers support (IE < 11)
                    this.constructor = this.constructor || WtReportAnalysis;
                }

                /**
                 * @ngdoc method
                 * @name WtReportAnalysis.validate
                 * @description
                 * @param {Object} config The configuration object to be validated
                 * @throws Error
                 */
                static validate(config) {
                    if (!_.hasIn(config, 'configJson') || !_.isObject(config.configJson)) {
                        throw Error('Report analysis missing analysis configuration');
                    }

                    if (!_.hasIn(config, 'id')) {
                        throw Error('Report analysis missing report element id');
                    }

                    if (!_.hasIn(config, 'reportId')) {
                        throw Error('Report analysis missing report id');
                    }

                    if (_.get(config, 'type') === 'ANALYSIS') {
                        super.validate(config.configJson);
                    }
                }

                /**
                 * @ngdoc method
                 * @name WtReportAnalysis#resetConfig_
                 * @description Internal method doing the actual reset
                 * @returns {WtReportAnalysis}
                 * @private
                 */
                resetConfig_() {
                    const config = this.pristineReportElementConfig;
                    this.pristineData_ = _.cloneDeep(ViewMode.resolveViewMode(config));
                    this.data_ = _.cloneDeep(ViewMode.resolveViewMode(config));
                    this.columns_ = null;
                    this.viewModes_ = null;
                    this.timeFilter_ = null;
                    this.activeViewModes_ = {};
                    this.init_();
                    return this;
                }

                /**
                 * @ngdoc method
                 * @name WtReportAnalysis#resetConfig
                 * @description Will reset the config of the current report analysis based on the
                 * pristine config of the analysis originally provided
                 * @returns {WtReportAnalysis}
                 */
                resetConfig() {
                    return this.resetConfig_().dispatchAction(this.actions.CONFIG_RESET);
                }

                /**
                 * @ngdoc property
                 * @name WtReportAnalysis#pristineConfig
                 * @description Exposes the pristine analysis configuration object to the outer world.
                 * Note that for analyses that hide the analysis configuration under configJson (report analyses, KPIs, Sparklines), the configJson value is returned by this property
                 * @returns {Object}
                 */
                get pristineConfig() {
                    return this.pristineData_.configJson;
                }

                /**
                 * @ngdoc property
                 * @name WtReportAnalysis#config
                 * @description Exposes the analysis configuration object to the outer world
                 * Note that for analyses that hide the analysis configuration under configJson (report analyses, KPIs, Sparklines), the configJson value is returned by this property
                 * @returns {Object}
                 */
                get config() {
                    return this.data_.configJson;
                }

                /**
                 * @ngdoc property
                 * @name WtReportAnalysis#pristineReportElementConfig
                 * @description Exposes the original report element configuration object to the outer world
                 * @returns {Object}
                 */
                get pristineReportElementConfig() {
                    return this.pristineData_;
                }

                /**
                 * @ngdoc property
                 * @name WtReportAnalysis#reportElementConfig
                 * @description Exposes the report element configuration object to the outer world
                 * @returns {Object}
                 */
                get reportElementConfig() {
                    return this.data_;
                }

                /**
                 * @ngdoc method
                 * @name WtReportAnalysis#getReportId
                 * @description Returns the report id
                 * @returns {number}
                 */
                getReportId() {
                    return _.get(this, 'data_.reportId');
                }

                /**
                 * @ngdoc method
                 * @name WtReportAnalysis#setReportId
                 * @description Sets a new report id
                 * @param {number} id The report id
                 * @returns {WtReportAnalysis}
                 */
                setReportId(id) {
                    if (id && _.isNumber(id)) {
                        _.set(this, 'data_.reportId', id);
                    }

                    return this;
                }

                /**
                 * @ngdoc method
                 * @name WtReportAnalysis#getId
                 * @description Returns the report element id
                 * @returns {string}
                 */
                getId() {
                    return _.get(this, 'data_.id');
                }

                /**
                 * @ngdoc method
                 * @name WtReportAnalysis#setId
                 * @description Sets the report element id
                 * @param {number} id The report element id
                 * @returns {WtReportAnalysis}
                 */
                setId(id) {
                    if (id && _.isNumber(id)) {
                        // TODO Set the configJson.id
                        _.set(this, 'data_.id', id);
                    }

                    return this;
                }

                /**
                 * @ngdoc method
                 * @name WtReportAnalysis#getPosition
                 * @description Returns the position of the report element in the report
                 * @returns {number}
                 */
                getPosition() {
                    return _.get(this, 'data_.position');
                }

                /**
                 * @ngdoc method
                 * @name WtReportAnalysis#setPosition
                 * @description Sets the report position
                 * @param {number} position The report element position
                 * @returns {WtReportAnalysis}
                 */
                setPosition(position) {
                    if (position && _.isNumber(position)) {
                        _.set(this, 'data_.position', position);
                    }

                    return this;
                }

                /**
                 * @ngdoc method
                 * @name WtReportAnalysis#getPositionAsPoint
                 * @description Retrieves the report element position as point inside the report configuration
                 * @returns {*}
                 */
                getPositionAsPoint() {
                    return _.get(this, 'data_.positionAsPoint');
                }

                /**
                 * @ngdoc method
                 * @name WtReportAnalysis#setPositionAsPoint
                 * @description Sets the report element position as point inside the report configuration
                 * @param {Object} iPosition The report element position as point inside the report configuration
                 * @returns {WtReportAnalysis}
                 */
                setPositionAsPoint(iPosition) {
                    const position = _.isPlainObject(iPosition) ? iPosition : {};

                    if (_.isNumber(position.x) && _.isNumber(position.y)) {
                        _.set(this, 'data_.positionAsPoint', position);
                    }

                    return this;
                }

                /**
                 * @ngdoc method
                 * @name WtReportAnalysis#getType
                 * @description Retrieves the report element type (ANALYSIS, KPI, SPARKLINE, etc.)
                 * @returns {string}
                 */
                getType() {
                    return _.get(this, 'data_.type');
                }

                /**
                 * @ngdoc method
                 * @name WtReportAnalysis#getWidth
                 * @description Retrieves the report element width in pixels
                 * @returns {number}
                 */
                getWidth() {
                    return _.get(this, 'data_.width');
                }

                /**
                 * @ngdoc method
                 * @name WtReportAnalysis#setWidth
                 * @description Sets the report element width in pixels
                 * @param {number} width The new report element width
                 * @returns {WtReportAnalysis}
                 */
                setWidth(width) {
                    if (width && _.isNumber(width)) {
                        _.set(this, 'data_.width', width);
                    }
                    return this;
                }

                /**
                 * @ngdoc method
                 * @name WtReportAnalysis#getHeight
                 * @description Retrieves the report element height
                 * @returns {number}
                 */
                getHeight() {
                    return _.get(this, 'data_.height');
                }

                /**
                 * @ngdoc method
                 * @name WtReportAnalysis#setHeight
                 * @description Sets a new report element height in pixels
                 * @param {number} height The report element height
                 * @returns {WtReportAnalysis}
                 */
                setHeight(height) {
                    if (height && _.isNumber(height)) {
                        _.set(this, 'data_.height', height);
                    }
                    return this;
                }
            };
        }]);
})(window.angular, window._);
